import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle, AlertTriangle, KeyRound, ArrowLeft } from "lucide-react";
import { apiErrorMsg } from "../lib/api";

export default function MagicLogin() {
  const { consumeMagic } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const token = params.get("token");
    const next = params.get("next") || "";
    if (!token) {
      setStatus("error");
      setError("Sign-in link is missing the token.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await consumeMagic(token);
        if (cancelled) return;
        setStatus("success");
        const u = data.user;
        const target = next || data.next_path || (
          u.role === "admin" ? "/admin" :
          u.role === "dealer" ? "/dealer" :
          u.role === "builder" ? "/builder" :
          u.role === "team" ? "/admin" :
          "/"
        );
        // small delay so success state is visible
        setTimeout(() => nav(target, { replace: true }), 700);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(apiErrorMsg(e, "This sign-in link is invalid or expired."));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#FAFAFA] px-6">
      <div className="w-full max-w-sm bg-white border border-[#E4E4E7] p-8 rounded-sm text-center" data-testid="magic-login-page">
        <KeyRound size={22} className="text-[#0055FF] mb-3 mx-auto" />
        {status === "verifying" && (
          <>
            <h1 className="font-heading font-bold text-2xl tracking-tight">Signing you in…</h1>
            <p className="text-[13px] text-[#52525B] mt-2">Aapki one-time link verify ho rahi hai.</p>
            <div className="mt-6 flex justify-center" data-testid="magic-verifying">
              <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
            </div>
          </>
        )}
        {status === "success" && (
          <div data-testid="magic-success">
            <CheckCircle size={36} className="text-[#15803D] mx-auto mb-2" />
            <h1 className="font-heading font-bold text-2xl tracking-tight">Signed in!</h1>
            <p className="text-[13px] text-[#52525B] mt-2">Redirecting…</p>
          </div>
        )}
        {status === "error" && (
          <div data-testid="magic-error">
            <AlertTriangle size={36} className="text-[#DC2626] mx-auto mb-2" />
            <h1 className="font-heading font-bold text-2xl tracking-tight">Link not valid</h1>
            <p className="text-[13px] text-[#52525B] mt-2">{error}</p>
            <Link to="/login" className="inline-block mt-4">
              <Button className="btn-brand rounded-sm" data-testid="magic-back-to-login">
                <ArrowLeft size={13} className="mr-1.5" /> Back to login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
